const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONEY_PLACEHOLDER = '—'; // em dash
const KG_PLACEHOLDER = '—'; // em dash

const moneyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const kgFormatter = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 });

// Siempre en hora argentina, aunque el navegador esté en otra zona.
const timeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'America/Argentina/Buenos_Aires',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/**
 * Presents a peso amount the way the API sends it (a plain number of pesos,
 * already converted from integer cents server-side). Never recomputes the
 * value, only formats it. Accepts a numeric string defensively since some
 * form state carries amounts that way before they reach a DTO.
 */
export function formatMoney(amount) {
  const value = typeof amount === 'string' ? Number(amount) : amount;
  if (typeof value !== 'number' || !Number.isFinite(value)) return MONEY_PLACEHOLDER;
  return moneyFormatter.format(value);
}

/**
 * Presents a kg amount (weight or training volume) with Argentine
 * grouping/decimal separators. Every kg figure the client shows — Progreso's
 * cards and per-exercise detail, Entrenar's completion summary, the admin's
 * member progress tab — is already computed server-side; this only formats
 * it, never recalculates it. Accepts a numeric string defensively, same as
 * `formatMoney`, since NUMERIC(6,2) values can arrive that way in some DTOs.
 */
export function formatKg(value) {
  const num = typeof value === 'string' ? Number(value) : value;
  if (typeof num !== 'number' || !Number.isFinite(num)) return KG_PLACEHOLDER;
  return `${kgFormatter.format(num)} kg`;
}

/** Número de kg sin unidad (`37,5`), para etiquetas compactas como `37,5×10`. */
export function formatKgNumber(value) {
  const num = typeof value === 'string' ? Number(value) : value;
  if (typeof num !== 'number' || !Number.isFinite(num)) return KG_PLACEHOLDER;
  return kgFormatter.format(num);
}

/**
 * Converts an API DATEONLY string (YYYY-MM-DD) to the DD/MM/AAAA the spec
 * requires for display, without ever touching the AAAA-MM-DD value that
 * travels back to the API. Never parses through `Date`, which would risk a
 * timezone-driven off-by-one day.
 */
export function formatDate(value) {
  if (typeof value !== 'string') return '';
  const match = DATE_ONLY_PATTERN.exec(value);
  if (!match) return '';
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

/**
 * HH:MM en hora argentina de un momento ISO que manda la API (por ejemplo, la
 * hora en que se registró un pago o un ingreso). Vacío si no hay un momento
 * válido.
 */
export function formatTime(value) {
  if (typeof value !== 'string' || !value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return timeFormatter.format(date);
}

/**
 * Fecha de un pago, con la hora del registro cuando la API la manda
 * (`recordedAt`). La API sólo la manda si el pago se registró el mismo día
 * de su fecha; en pagos importados o cargados con otra fecha va sólo el día.
 */
export function formatPaymentDate(payment) {
  const date = formatDate(payment?.paidOn);
  const time = formatTime(payment?.recordedAt);
  return time ? `${date} · ${time}` : date;
}

// Mirrors api/src/gym/membership-service.js#serializeMembership's `status`
// values and the Spanish labels from the design spec ("al día, pendiente,
// vencida, próxima o finalizada"). `tone` is a presentation hint a badge
// component can map to color/icon without re-deriving membership state.
const MEMBERSHIP_STATUSES = {
  active: { label: 'Al día', tone: 'success' },
  pending: { label: 'Pendiente', tone: 'warning' },
  overdue: { label: 'Vencida', tone: 'danger' },
  upcoming: { label: 'Próxima', tone: 'info' },
  expired: { label: 'Finalizada', tone: 'neutral' },
  // Período anulado por recepción: sigue en el historial pero no cuenta para nada.
  voided: { label: 'Anulado', tone: 'neutral' },
  // Socio sin ningún período (lista de ingresos del admin).
  none: { label: 'Sin plan', tone: 'neutral' },
};

// Estados de un pago. 'voided' aparece cuando se anuló el período al que pertenecía.
export const PAYMENT_STATUS_LABELS = {
  confirmed: 'Confirmado', pending: 'Pendiente', rejected: 'Rechazado', voided: 'Anulado',
};

export function membershipStatus(status) {
  const known = typeof status === 'string' ? MEMBERSHIP_STATUSES[status] : undefined;
  if (!known) return { code: 'unknown', label: 'Sin información', tone: 'neutral' };
  return { code: status, ...known };
}

// Shared with MemberDetailPage (read-only payment history), PaymentForm
// (method select) and PendingPaymentBanner (pending payload display) so the
// four `POST /payments` method values only get one Spanish label mapping.
export const PAYMENT_METHOD_LABELS = {
  cash: 'Efectivo', transfer: 'Transferencia', card: 'Tarjeta', other: 'Otro',
};
